import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Typography,
  Button,
  MatchOverview,
  CountryFlag,
  HowToScoreModal,
  Info,
  ChevronUp,
  PitchBackground,
  TeamShirt,
  GameKeysAccordion,
  TabItem,
  TabGroup,
  SubstitutesCard,
  FixtureGroup,
  TeamOverview,
  Goal,
  Assist,
  Ticket,
  TransferInOut,
  PenaltyMiss,
  PenaltySave,
} from '@dreamteam-monorepo/ui';
import { LockedPlayerInfoModal } from '@/pages/Teams/components/LockedPlayerInfoModal';
import type { BEPlayerDetail } from '@/lib/network/players';
import { MatchFormation } from './components/MatchFormation';
import {
  trackFantasyGameKeys,
  trackFantasyHowToScoreClose,
  trackFantasyHowToScoreTab,
} from '@/hooks/useGa4Tracking';
import { trackFantasyEvent } from '@/analytics';

import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';
import { SponsorBanner } from '@/components/SponsorBanner';
import { ContentAdSlot } from '@/ads';
import { useMatchData } from './hooks/useMatchData';
import { useTeamOverview } from './hooks/useTeamOverview';
import { BackButton } from './components/BackButton';
import { MatchInfoWithIcons } from './components/MatchEvents';
import { formatUkTime } from '@/lib/ukTime';
import { useTeamCreateTournamentCalendarId } from '@/hooks/useTeamCreateTournamentCalendarId';
import { useGoBack } from '@/hooks/useGoBack';
import { periodAxisFromFormat } from '@/lib/periodLabels';
import { useModalsStore } from '@/stores/ModalsStore';
import {
  DESKTOP_SUBS_PANEL_PADDING_CLASSNAME,
  NAVBAR_HEIGHT_PX,
  SUBS_PANEL_TOP_OFFSET_PX,
} from './constants';
import type { Player } from './types';
import {
  buildMatchPlayerDetail,
  convertPlayersToSubstituteRows,
  isBeforeLineupsAnnouncement,
  toModalPlayer,
} from './utils';

const NON_EPL_FALLBACK_FORMATION = [1, 4, 4, 2];

const tabs: TabItem[] = [
  { id: '1', label: 'Line Ups' },
  { id: '2', label: 'Team Stats' },
];

const LineupSkeleton = ({ pulsing = false }: { pulsing?: boolean }) => (
  <div
    className={twMerge(
      'flex flex-col items-center justify-center h-full',
      pulsing && 'animate-pulse'
    )}
  >
    <div className="flex flex-col items-center gap-y-4 md:gap-y-5">
      {[4, 4, 2, 1].map((row, rowIndex) => (
        <div key={rowIndex} className="flex flex-row gap-x-2 md:gap-x-5">
          {Array.from({ length: row }).map((_, colIndex) => (
            <div key={colIndex} className="h-16 w-16 md:h-20 md:w-20 rounded-md bg-white/20" />
          ))}
        </div>
      ))}
    </div>
  </div>
);

const generatePlaceholderTeamMembers = (formation: number[]) => {
  const totalPlayers = formation.reduce((sum, count) => sum + count, 0);
  return Array.from({ length: totalPlayers }, (_, index) => ({
    id: `placeholder-${index}`,
    name: '',
    desc: 'N/A',
    mode: 'disabled' as const,
    team: undefined,
    position: undefined,
  }));
};

export default function MatchDetail() {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  // Pop the history entry so Match Centre keeps the filters it was left with.
  const goBack = useGoBack('/match-centre');
  const [activeTab, setActiveTab] = useState<'lineups' | 'stats'>('lineups');
  const [selectedTeam, setSelectedTeam] = useState<'home' | 'away'>('home');
  const [ptsView, setPtsView] = useState<'pts' | 'ppm'>('pts');
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const [howToScoreOpen, setHowToScoreOpen] = useState(false);
  const [isMobileSubsOpen, setIsMobileSubsOpen] = useState(false);
  const [selectedTabId, setSelectedTabId] = useState(tabs[0].id);
  const [gameKeyExpanded, setGameKeyExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { format: tournamentFormat } = useTeamCreateTournamentCalendarId();
  const isGameweekFormat = periodAxisFromFormat(tournamentFormat) === 'gameweek';
  const [selectedPlayer, setSelectedPlayer] = useState<(typeof currentPlayers)[number] | null>(
    null
  );
  const { isPlayerModalOpen, setIsPlayerModalOpen } = useModalsStore();

  const { displayMatch, isLoading, homeHasLineup, awayHasLineup, isError, errorMessage } =
    useMatchData({
      matchId,
    });
  const { teamOverviewProps } = useTeamOverview({ matchId, displayMatch });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // An additional-league fixture (e.g. a cup adopted under the EPL umbrella)
  // is rejected by the single-match API with a 403. Show a clear "navigate
  // back to Match Centre" state instead of partial data from a competition
  // the app does not support Match Detail for.
  if (isError) {
    return (
      <div className="components light min-h-[calc(100vh-52px)] flex flex-col items-center justify-center px-4 py-8">
        <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-xl bg-white p-6 shadow-[0px_4px_24px_0px_rgba(34,37,38,0.2)]">
          <Typography variant="subheadline020" className="text-center text-[--color-neutral-900]">
            {errorMessage || 'Match detail is not available.'}
          </Typography>
          <Button variant="contained" color="blue" onClick={goBack}>
            Navigate Back
          </Button>
        </div>
      </div>
    );
  }

  if (!displayMatch) {
    return (
      <div className="components light min-h-[calc(100vh-52px)] flex items-center justify-center">
        <Typography variant="subheadline020" className="text-[--color-neutral-900]">
          Loading match...
        </Typography>
      </div>
    );
  }

  const currentPlayers =
    selectedTeam === 'home' ? displayMatch.homePlayers : displayMatch.awayPlayers;
  const currentFormation =
    selectedTeam === 'home' ? displayMatch.homeFormation : displayMatch.awayFormation;
  const currentSubs = selectedTeam === 'home' ? displayMatch.homeSubs : displayMatch.awaySubs;
  const selectedTeamHasLineup = selectedTeam === 'home' ? homeHasLineup : awayHasLineup;
  const selectedTeamIsFantasy =
    selectedTeam === 'home' ? displayMatch.homeHasFantasyData : displayMatch.awayHasFantasyData;
  const matchTeamsLabel = `${displayMatch.homeTeam.name} v ${displayMatch.awayTeam.name}`;

  const isMatchPlayed = displayMatch.hasStarted;

  const trackMatchDetailLink = (linkName: string) => {
    trackFantasyEvent('matchCentreLink', {
      link_name: `${linkName} - ${matchTeamsLabel}`,
    });
  };

  const handlePlayerPress = (player: Player) => {
    if (!selectedTeamIsFantasy) return;
    if (isMatchPlayed) {
      setSelectedPlayer(player);
      setIsPlayerModalOpen(true);
    }
  };

  const effectiveFormation = selectedTeamIsFantasy ? currentFormation : NON_EPL_FALLBACK_FORMATION;

  const teamMembers = selectedTeamIsFantasy
    ? currentPlayers.map(player => ({
        id: player.id,
        name: player.name,
        desc: isMatchPlayed
          ? ptsView === 'pts'
            ? `${(player.basicPoints ?? 0) + (player.ppmPoints ?? 0)}`
            : `${player.breakdown?.ppms_earned ?? 0}`
          : (player.position ?? `${player.number}`),
        team: player.team,
        mode: 'select' as const,
        shirtKey: player.shirtKey,
        position: player.position,
        matchEvents: player.events,
      }))
    : generatePlaceholderTeamMembers(effectiveFormation);

  const handleChangeTab = (tabId: string) => {
    const nextTab = tabId === '1' ? 'Lineups' : 'Stats';
    trackMatchDetailLink(nextTab);
    setSelectedTabId(tabId);
    setActiveTab(tabId === '1' ? 'lineups' : 'stats');
  };

  const handlePointsViewChange = (nextView: 'pts' | 'ppm') => {
    if (nextView === ptsView) return;

    const selectedTeamName =
      selectedTeam === 'home' ? displayMatch.homeTeam.name : displayMatch.awayTeam.name;
    trackMatchDetailLink(`${nextView === 'pts' ? 'Points' : 'PPM'} - ${selectedTeamName}`);
    setPtsView(nextView);
  };

  const handleSelectedTeamChange = (nextTeam: 'home' | 'away') => {
    if (nextTeam === selectedTeam) return;

    const nextTeamName =
      nextTeam === 'home' ? displayMatch.homeTeam.name : displayMatch.awayTeam.name;
    trackMatchDetailLink(`Team - ${nextTeamName}`);
    setSelectedTeam(nextTeam);
  };

  const beforeLineups = isBeforeLineupsAnnouncement(displayMatch.kickoffAt);

  const showSubstitutesPanel =
    activeTab === 'lineups' && !beforeLineups && selectedTeamIsFantasy && selectedTeamHasLineup;

  return (
    <div className="components light min-h-[calc(100vh-52px)] flex flex-col">
      {/* Match Header */}
      <div className="bg-white px-[--padding-md] md:px-[--padding-7xl] py-3 shadow-sm flex-none">
        <div className="max-w-[1280px] mx-auto">
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
            {/* Left Column */}
            <div className="flex-1 lg:max-w-[765px]">
              <div className="flex items-center justify-between mb-3">
                <BackButton onClick={goBack} />
                <Typography
                  variant="subheadline020"
                  className="bg-[#eae9fd] rounded-[--corner-xs] px-2 py-1 text-[#222526]"
                >
                  {displayMatch.competition}
                </Typography>
                <div />
              </div>

              {/* Match Overview Card */}
              <div className="bg-[#eae9fd] rounded-[--corner-xs] px-4 md:px-10 py-4">
                {displayMatch.hasStarted ? (
                  <MatchOverview
                    leftTeam={{
                      name: displayMatch.homeTeam.name,
                      logo: displayMatch.homeTeam.flag ? (
                        <CountryFlag
                          country={displayMatch.homeTeam.flag}
                          className="w-6 h-6 rounded-full"
                        />
                      ) : (
                        <TeamShirt
                          team={displayMatch.homeTeam.code}
                          className="h-6 w-6"
                          assetScale="1x"
                        />
                      ),
                      score: displayMatch.homeTeam.score,
                    }}
                    rightTeam={{
                      name: displayMatch.awayTeam.name,
                      logo: displayMatch.awayTeam.flag ? (
                        <CountryFlag
                          country={displayMatch.awayTeam.flag}
                          className="w-6 h-6 rounded-full"
                        />
                      ) : (
                        <TeamShirt
                          team={displayMatch.awayTeam.code}
                          className="h-6 w-6"
                          assetScale="1x"
                        />
                      ),
                      score: displayMatch.awayTeam.score,
                    }}
                    status={displayMatch.status}
                    statusVariant={displayMatch.statusVariant}
                  />
                ) : (
                  <div className="flex flex-1 items-center justify-center">
                    <div className="flex items-center gap-4 w-fit">
                      <div className="flex items-center gap-2">
                        <Typography
                          variant="subheadline020"
                          className="text-[#222526] leading-tight"
                        >
                          {displayMatch.homeTeam.name}
                        </Typography>

                        {displayMatch.homeTeam.flag ? (
                          <CountryFlag
                            country={displayMatch.homeTeam.flag}
                            className="w-6 h-6 rounded-full shrink-0"
                          />
                        ) : (
                          <TeamShirt
                            team={displayMatch.homeTeam.code}
                            className="h-6 w-6"
                            assetScale="1x"
                          />
                        )}
                      </div>

                      <div className="shrink-0 bg-[var(--match-kickoff-bg,#ffffff)] px-[--padding-2xs] py-[--padding-3xs] rounded-[var(--corner-xs,4px)] flex items-center justify-center">
                        <Typography
                          as="span"
                          variant="subheadline010"
                          className="text-[var(--match-kickoff-text,#222526)] whitespace-nowrap"
                        >
                          {formatUkTime(displayMatch.kickoffAt)}
                        </Typography>
                      </div>

                      <div className="flex items-center gap-2">
                        {displayMatch.awayTeam.flag ? (
                          <CountryFlag
                            country={displayMatch.awayTeam.flag}
                            className="w-6 h-6 rounded-full shrink-0"
                          />
                        ) : (
                          <TeamShirt
                            team={displayMatch.awayTeam.code}
                            className="h-6 w-6"
                            assetScale="1x"
                          />
                        )}

                        <Typography
                          variant="subheadline020"
                          className="text-[#222526] leading-tight"
                        >
                          {displayMatch.awayTeam.name}
                        </Typography>
                      </div>
                    </div>
                  </div>
                )}

                {displayMatch.hasStarted && detailsExpanded && (
                  <div className="mt-3">
                    <MatchInfoWithIcons eventSections={displayMatch.eventSections} />
                  </div>
                )}

                {displayMatch.hasStarted && (
                  <button
                    onClick={() => {
                      const nextExpanded = !detailsExpanded;
                      trackFantasyEvent(
                        nextExpanded ? 'matchDetailsExpanded' : 'matchDetailsCollapsed'
                      );
                      setDetailsExpanded(nextExpanded);
                    }}
                    className="flex items-center gap-2 mx-auto mt-2 text-[--color-blue-200] hover:opacity-70 transition-opacity overflow-visible"
                  >
                    <Typography variant="subheadline010" className="text-[--color-blue-200]">
                      {detailsExpanded ? 'Hide Details' : 'Show Details'}
                    </Typography>
                    <div className="flex items-center justify-center w-5 h-5 overflow-visible">
                      <ChevronUp
                        className={twMerge(
                          'w-5 h-5 text-[--color-blue-200] transition-transform duration-300 ease-in-out will-change-transform',
                          detailsExpanded ? 'rotate-0' : 'rotate-180'
                        )}
                      />
                    </div>
                  </button>
                )}
              </div>

              {/* Tabs */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-3 w-full">
                <div className="flex justify-center md:justify-start w-full md:w-auto">
                  <TabGroup tabs={tabs} onChangeTab={handleChangeTab} selectedId={selectedTabId} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {activeTab === 'lineups' ? (
        <>
          <PitchBackground
            className="flex-1 min-h-[900px] lg:min-h-[600px] flex flex-col"
            variant="green"
            frame
          >
            <SponsorBanner variant="brand-strip" />

            {/* Team Selector */}
            <div className="w-full flex gap-4">
              {isMatchPlayed && (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => handlePointsViewChange('pts')}
                    variant="outlined"
                    color="main"
                    size="small"
                    className={clsx(
                      'rounded-full w-[55px] md:!w-[70px] md:!h-[36px] md:!min-w-[70px] px-3 md:px-4 py-1 md:py-2 border-2 box-border shrink-0 flex items-center justify-center',
                      ptsView === 'pts'
                        ? '!bg-[--tab-bg-selected] !text-[--tab-label-selected] !border-[--tab-border-selected-filled]'
                        : '!bg-transparent !text-[--tab-label-default] !border-[--tab-border-default] hover:!bg-[--tab-bg-hover]'
                    )}
                  >
                    Pts
                  </Button>

                  <Button
                    onClick={() => handlePointsViewChange('ppm')}
                    variant="outlined"
                    color="main"
                    size="small"
                    className={clsx(
                      'rounded-full w-[55px] md:!w-[70px] md:!h-[36px] md:!min-w-[70px] px-3 md:px-4 py-1 md:py-2 border-2 box-border shrink-0 flex items-center justify-center',
                      ptsView === 'ppm'
                        ? '!bg-[--tab-bg-selected] !text-[--tab-label-selected] !border-[--tab-border-selected-filled]'
                        : '!bg-transparent !text-[--tab-label-default] !border-[--tab-border-default] hover:!bg-[--tab-bg-hover]'
                    )}
                  >
                    PPM
                  </Button>
                </div>
              )}

              <div className="flex flex-1 items-center justify-end lg:justify-start lg:pl-10 gap-2 p-3">
                <Button
                  onClick={() => handleSelectedTeamChange('home')}
                  variant="outlined"
                  color="main"
                  size="small"
                  className={clsx(
                    'rounded-full px-4 py-2 border-2 md:w-[140px] shrink-0',
                    selectedTeam === 'home'
                      ? '!bg-[--tab-bg-selected] !text-[--tab-label-selected] !border-[--tab-border-selected-filled]'
                      : '!bg-transparent !text-[--tab-label-default] !border-[--tab-border-default] hover:!bg-[--tab-bg-hover]'
                  )}
                >
                  {displayMatch.homeTeam.code}
                </Button>
                <Button
                  onClick={() => handleSelectedTeamChange('away')}
                  variant="outlined"
                  color="main"
                  size="small"
                  className={clsx(
                    'rounded-full px-4 py-2 border-2 md:w-[140px] shrink-0',
                    selectedTeam === 'away'
                      ? '!bg-[--tab-bg-selected] !text-[--tab-label-selected] !border-[--tab-border-selected-filled]'
                      : '!bg-transparent !text-[--tab-label-default] !border-[--tab-border-default] hover:!bg-[--tab-bg-hover]'
                  )}
                >
                  {displayMatch.awayTeam.code}
                </Button>
              </div>
            </div>

            <div
              className={clsx([
                'py-4 lg:py-20 px-[--padding-md] md:px-0 flex flex-1 flex-col justify-center gap-[--padding-xl] md:gap-[--padding-2xl]',
                DESKTOP_SUBS_PANEL_PADDING_CLASSNAME,
              ])}
            >
              {isLoading ? (
                <LineupSkeleton pulsing />
              ) : selectedTeamIsFantasy && (beforeLineups || !selectedTeamHasLineup) ? (
                <div className="flex flex-col items-center justify-center h-full gap-6">
                  <Typography
                    variant="subheadline020"
                    className="text-center text-white bg-black/40 rounded-[--corner-md] px-6 py-4 backdrop-blur-sm"
                  >
                    Lineups will be revealed from 75 minutes before kick-off.
                  </Typography>
                  <LineupSkeleton />
                </div>
              ) : (
                <>
                  {/* Desktop Substitutes Panel */}
                  {showSubstitutesPanel && (
                    <div
                      className={clsx(
                        'hidden lg:flex lg:flex-col lg:gap-4 md:absolute top-14 z-[20] ',
                        'right-[--padding-7xl]'
                      )}
                      style={{
                        // top: `${NAVBAR_HEIGHT_PX + SUBS_PANEL_TOP_OFFSET_PX}px`,
                        height: `calc(100vh - ${NAVBAR_HEIGHT_PX + SUBS_PANEL_TOP_OFFSET_PX + 20}px)`,
                        width: '411px',
                      }}
                    >
                      <SubstitutesCard
                        className="h-full"
                        rows={convertPlayersToSubstituteRows(currentSubs, handlePlayerPress)}
                        onHowToScore={() => setHowToScoreOpen(true)}
                        gameKeyExpanded={gameKeyExpanded}
                        onGameKeyExpandedChange={setGameKeyExpanded}
                        gameKeyContent={
                          <div className="grid grid-cols-3 gap-[--padding-xs] p-[--padding-md]">
                            <div className="flex flex-1 flex-row gap-[--padding-xs] items-center">
                              <Goal className="h-[20px] w-[20px]  text-[--color-neurtal-1000]" />
                              <Typography variant="paragraph020" className="text-[#222526] text-xs">
                                Goal
                              </Typography>
                            </div>
                            <div className="flex flex-1 flex-row gap-[--padding-xs] items-center">
                              <Assist className="h-[20px] w-[20px] text-[--color-neurtal-1000]" />
                              <Typography variant="paragraph020" className="text-[#222526] text-xs">
                                Assist
                              </Typography>
                            </div>
                            <div className="flex flex-1 flex-row gap-[--padding-xs] items-center">
                              <Goal className="h-[20px] w-[20px] text-[--color-red-200]" />
                              <Typography variant="paragraph020" className="text-[#222526] text-xs">
                                Own Goal
                              </Typography>
                            </div>
                            <div className="flex flex-1 flex-row gap-[--padding-xs] items-center">
                              <Ticket ticketType="yellow" className="h-[20px] w-[20px]" />
                              <Typography variant="paragraph020" className="text-[#222526] text-xs">
                                Yellow
                              </Typography>
                            </div>
                            <div className="flex flex-1 flex-row gap-[--padding-xs] items-center">
                              <Ticket ticketType="red" className="h-[20px] w-[20px]" />
                              <Typography variant="paragraph020" className="text-[#222526] text-xs">
                                Red
                              </Typography>
                            </div>
                            <div className="flex flex-1 flex-row gap-[--padding-xs] items-center">
                              <TransferInOut className="h-[20px] w-[20px] text-[--color-green-200] rotate-0" />
                              <Typography variant="paragraph020" className="text-[#222526] text-xs">
                                Sub On
                              </Typography>
                            </div>
                            <div className="flex flex-1 flex-row gap-[--padding-xs] items-center">
                              <TransferInOut className="h-[20px] w-[20px] text-[--color-red-200] rotate-180" />
                              <Typography variant="paragraph020" className="text-[#222526] text-xs">
                                Sub Off
                              </Typography>
                            </div>
                            <div className="flex flex-1 flex-row gap-[--padding-xs] items-center">
                              <PenaltyMiss className="h-[20px] w-[20px] text-[--color-red-200]" />
                              <Typography variant="paragraph020" className="text-[#222526] text-xs">
                                Penalty Miss
                              </Typography>
                            </div>
                            <div className="flex flex-1 flex-row gap-[--padding-xs] items-center">
                              <PenaltySave className="h-[20px] w-[20px] text-[--color-blue-200]" />
                              <Typography variant="paragraph020" className="text-[#222526] text-xs">
                                Penalty Save
                              </Typography>
                            </div>
                          </div>
                        }
                      />
                    </div>
                  )}

                  <MatchFormation
                    className="md:absolute md:top-24 sm:left-36 md:left-32 xl:left-[280px] 2xl:left-[300px] mx-auto justify-center"
                    formation={effectiveFormation}
                    members={teamMembers}
                    onPressPlayer={player => {
                      const originalPlayer = currentPlayers.find(p => p.id === player.id);
                      if (originalPlayer) {
                        handlePlayerPress(originalPlayer);
                      }
                    }}
                    actions={
                      <div className="flex flex-col pt-6">
                        {/* Mobile Buttons */}
                        <div className="lg:hidden flex items-center justify-center gap-3 w-full px-4 mt-4">
                          {selectedTeamIsFantasy && selectedTeamHasLineup && (
                            <Button
                              onClick={() => setIsMobileSubsOpen(true)}
                              variant="contained"
                              color="blue"
                              size="small"
                              className="flex-1 rounded-[--corner-xs] py-3"
                            >
                              Subs
                            </Button>
                          )}
                          <Button
                            onClick={() => setHowToScoreOpen(true)}
                            variant="outlined"
                            color="main"
                            size="small"
                            className="flex-1 rounded-[--corner-xs] py-3 bg-white whitespace-nowrap"
                          >
                            <span className="flex items-center justify-center gap-2">
                              How To Score
                              <Info className="size-4 text-[--color-neutral-900] shrink-0" />
                            </span>
                          </Button>
                        </div>

                        {/* Mobile In-game icons */}
                        <GameKeysAccordion
                          className="w-full max-w-[358px] bg-white mx-auto lg:hidden mt-4 mb-4 px-1"
                          title="GAME KEY"
                          collapsible
                          gameKeys={[
                            'Assist',
                            'Goal',
                            'Own Goal',
                            'Penalty Miss',
                            'Penalty Save',
                            'Red',
                            'Sub Off',
                            'Sub On',
                            'Yellow',
                          ]}
                          onToggle={trackFantasyGameKeys}
                        />
                      </div>
                    }
                  />
                </>
              )}
            </div>
          </PitchBackground>

          {/* Mobile Subs Bottom Sheet */}
          {isMobileSubsOpen && (
            <>
              {/* Backdrop */}
              <div
                className="lg:hidden fixed inset-0 z-[100] bg-black/50 animate-in fade-in duration-200"
                onClick={() => setIsMobileSubsOpen(false)}
              />
              {/* Sheet */}
              <div className="lg:hidden fixed bottom-0 left-0 right-0 z-[101] max-h-[80vh] rounded-t-2xl bg-white shadow-[0_-4px_20px_rgba(0,0,0,0.15)] flex flex-col animate-in slide-in-from-bottom duration-300">
                {/* Drag handle */}
                <div className="flex justify-center pt-3 pb-1">
                  <div className="w-10 h-1 rounded-full bg-[--color-neutral-300]" />
                </div>
                <div className="flex-1 overflow-y-auto px-4 pb-4">
                  <SubstitutesCard
                    rows={convertPlayersToSubstituteRows(currentSubs, handlePlayerPress)}
                    title="Subs"
                    onClose={() => setIsMobileSubsOpen(false)}
                  />
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <div
          className={clsx(
            'flex-1 bg-white px-[--padding-md] md:px-[--padding-7xl] overflow-y-auto'
          )}
        >
          <div className="max-w-[1280px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Stats Column */}
              <div className="flex-1 lg:max-w-[765px]">
                <FixtureGroup
                  version="match stats"
                  stats={[
                    {
                      label: 'Possession',
                      homeValue: displayMatch.hasStarted
                        ? `${displayMatch.stats.possession[0]}%`
                        : '-',
                      awayValue: displayMatch.hasStarted
                        ? `${displayMatch.stats.possession[1]}%`
                        : '-',
                    },
                    {
                      label: 'Pass Completion Rate',
                      homeValue: displayMatch.hasStarted
                        ? `${displayMatch.stats.passCompletion[0]}%`
                        : '-',
                      awayValue: displayMatch.hasStarted
                        ? `${displayMatch.stats.passCompletion[1]}%`
                        : '-',
                    },
                    {
                      label: 'Shots',
                      homeValue: displayMatch.hasStarted ? displayMatch.stats.shots[0] : '-',
                      awayValue: displayMatch.hasStarted ? displayMatch.stats.shots[1] : '-',
                    },
                    {
                      label: 'Shots On Target',
                      homeValue: displayMatch.hasStarted
                        ? displayMatch.stats.shotsOnTarget[0]
                        : '-',
                      awayValue: displayMatch.hasStarted
                        ? displayMatch.stats.shotsOnTarget[1]
                        : '-',
                    },
                    {
                      label: 'Corners',
                      homeValue: displayMatch.hasStarted ? displayMatch.stats.corners[0] : '-',
                      awayValue: displayMatch.hasStarted ? displayMatch.stats.corners[1] : '-',
                    },
                    {
                      label: 'Fouls',
                      homeValue: displayMatch.hasStarted ? displayMatch.stats.fouls[0] : '-',
                      awayValue: displayMatch.hasStarted ? displayMatch.stats.fouls[1] : '-',
                    },
                    {
                      label: 'Tackles',
                      homeValue: displayMatch.hasStarted ? displayMatch.stats.tackles[0] : '-',
                      awayValue: displayMatch.hasStarted ? displayMatch.stats.tackles[1] : '-',
                    },
                    {
                      label: 'Saves',
                      homeValue: displayMatch.hasStarted ? displayMatch.stats.saves[0] : '-',
                      awayValue: displayMatch.hasStarted ? displayMatch.stats.saves[1] : '-',
                    },
                    {
                      label: 'Yellow Cards',
                      homeValue: displayMatch.hasStarted ? displayMatch.stats.yellowCards[0] : '-',
                      awayValue: displayMatch.hasStarted ? displayMatch.stats.yellowCards[1] : '-',
                    },
                    {
                      label: 'Red Cards',
                      homeValue: displayMatch.hasStarted ? displayMatch.stats.redCards[0] : '-',
                      awayValue: displayMatch.hasStarted ? displayMatch.stats.redCards[1] : '-',
                    },
                  ]}
                />

                {/* Season Stats */}
                {!displayMatch.isAdditionalLeagueFixture &&
                  displayMatch.seasonStats &&
                  displayMatch.seasonStats.length > 0 && (
                    <div className="mt-8">
                      <FixtureGroup version="season stats" stats={displayMatch.seasonStats} />
                    </div>
                  )}
              </div>

              {/* Team Overview */}
              {teamOverviewProps && (
                <div className="w-full max-w-[450px] mx-auto px-4">
                  <TeamOverview {...teamOverviewProps} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ContentAdSlot id="ad-boa-leaderboard" className="bg-white" />

      {/* How To Score Modal */}
      <HowToScoreModal
        open={howToScoreOpen}
        onOpenChange={open => {
          if (!open && howToScoreOpen) trackFantasyHowToScoreClose();
          setHowToScoreOpen(open);
        }}
        onSelectedTabChange={trackFantasyHowToScoreTab}
        hidePlayerTabs={isMobile}
      />

      {/* Player Match Card Modal */}
      {selectedPlayer &&
        (() => {
          const { playerMatches, matchdayStats } = buildMatchPlayerDetail(
            selectedPlayer,
            displayMatch
          );

          return (
            <LockedPlayerInfoModal
              open={isPlayerModalOpen}
              onOpenChange={setIsPlayerModalOpen}
              player={toModalPlayer(selectedPlayer)}
              lockedPlayerDetails={{ playerMatches, matchdayStats } as BEPlayerDetail}
              periodStats={matchdayStats}
              effectivePeriodPoints={matchdayStats.points ?? 0}
              periodLabelShort={isGameweekFormat ? 'Gameweek' : 'Matchday'}
              onFullProfile={() => {
                setIsPlayerModalOpen(false);
                navigate(`/player/${selectedPlayer.fantasyPlayerId ?? selectedPlayer.id}`);
              }}
            />
          );
        })()}

      {/* Bottom padding */}
      <div className="h-[--padding-xl]" />
    </div>
  );
}
